import {
  createSlice,
  PayloadAction,
  SerializedError,
  Slice,
} from "@reduxjs/toolkit"
import {
  AssociatedChannel,
  AssociatedIdentifier,
  AsyncOperationStore,
  Attribute,
  ChannelInfo,
  ConflictEventWrapper,
  DebugInfo,
  FeatureFlagEligibilityCheck,
  initialState,
  LocaleOverride,
  ObjectDataStore,
  Store,
  SubscriptionListPayload,
  SupportInfo,
  Tag,
} from "./types"
import * as thunks from "./thunks"
import { PreferenceCenter } from "@ua/web-sdk/lib/remote-data/preference-center"
import { ScopedSubscriptionListIdentifier } from "@ua/web-sdk/lib/types"
import { PrivacyManagerFeature } from "@ua/web-sdk/lib/privacy-manager/types"

/**
 * Utilities
 */

function processPendingOperation<T extends AsyncOperationStore>(
  state: T
): void {
  state.status = "pending"
  state.lastUpdated = Date.now()
  delete state.error
}

function processFulfilledOperation<T extends AsyncOperationStore, V>(
  state: T,
  action: PayloadAction<V>,
  reducer: (state: T, action: PayloadAction<V>) => void
): void {
  state.status = "fulfilled"
  state.lastUpdated = Date.now()
  delete state.error
  return reducer(state, action)
}

function processRejectedOperation<T extends AsyncOperationStore>(
  state: T,
  action: PayloadAction<unknown, string, unknown, SerializedError>
): void {
  state.status = "rejected"
  state.lastUpdated = Date.now()
  state.error = action.error.message ?? "unknown error"
}

/**
 * ObjectDataStore common
 */

const addTag = (state: ObjectDataStore, action: PayloadAction<Tag>): void => {
  state.tags.push(action.payload)
}

const removeTag = (
  state: ObjectDataStore,
  action: PayloadAction<Tag>
): void => {
  console.log("removing tag", action.payload)
  state.tags = state.tags.filter(({ id }) => id !== action.payload.id)
}

const setAttribute = (
  state: ObjectDataStore,
  action: PayloadAction<Attribute>
): void => {
  state.attributes[action.payload.key] = action.payload
}

const removeAttribute = (
  state: ObjectDataStore,
  action: PayloadAction<Attribute>
): void => {
  delete state.attributes[action.payload.key]
}

/**
 * Debug slice
 */
export type DebugSliceState = Store["debug"]

const setDebugInfo = (
  state: DebugSliceState,
  action: PayloadAction<DebugInfo>
) => {
  state.debugInfo = action.payload
}

const setIaaDisplayPausedState = (
  state: DebugSliceState,
  action: PayloadAction<boolean>
) => {
  if (!state.debugInfo) return
  state.debugInfo.iaaDisplayPausedState = action.payload
}

const setPrivacyManagerFeatures = (
  state: DebugSliceState,
  action: PayloadAction<PrivacyManagerFeature[]>
) => {
  if (!state.debugInfo) return
  state.debugInfo.privacyManagerFeatures = action.payload
}

const debugReducers = {
  setDebugInfo,
  setIaaDisplayPausedState,
}

export type DebugSlice = Slice<DebugSliceState, typeof debugReducers, "debug">

export const debugSlice = createSlice({
  name: "debug",
  initialState: initialState.debug,
  reducers: debugReducers,
  extraReducers: (builder) => {
    builder.addCase(thunks.loadDebugInfo.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.loadDebugInfo.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setDebugInfo)
    )
    builder.addCase(thunks.loadDebugInfo.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.toggleIaaDisplayPaused.pending, (state, action) => {
      processPendingOperation(state)
    })
    builder.addCase(
      thunks.toggleIaaDisplayPaused.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, setIaaDisplayPausedState)
      }
    )
    builder.addCase(thunks.toggleIaaDisplayPaused.rejected, (state, action) => {
      processRejectedOperation(state, action)
    })

    builder.addCase(
      thunks.enablePrivacyManagerFeatures.pending,
      (state, action) => {
        processPendingOperation(state)
      }
    )
    builder.addCase(
      thunks.enablePrivacyManagerFeatures.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, setPrivacyManagerFeatures)
      }
    )
    builder.addCase(
      thunks.enablePrivacyManagerFeatures.rejected,
      (state, action) => {
        processRejectedOperation(state, action)
      }
    )

    builder.addCase(
      thunks.disablePrivacyManagerFeatures.pending,
      (state, action) => {
        processPendingOperation(state)
      }
    )
    builder.addCase(
      thunks.disablePrivacyManagerFeatures.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, setPrivacyManagerFeatures)
      }
    )
    builder.addCase(
      thunks.disablePrivacyManagerFeatures.rejected,
      (state, action) => {
        processRejectedOperation(state, action)
      }
    )

    builder.addCase(
      thunks.loadPrivacyManagerFeatures.pending,
      (state, action) => {
        processPendingOperation(state)
      }
    )
    builder.addCase(
      thunks.loadPrivacyManagerFeatures.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, setPrivacyManagerFeatures)
      }
    )
    builder.addCase(
      thunks.loadPrivacyManagerFeatures.rejected,
      (state, action) => {
        processRejectedOperation(state, action)
      }
    )
  },
})

/**
 * Metadata slice
 */

export type MetadataSliceState = Store["metadata"]

const setLocaleOverride = (
  state: MetadataSliceState,
  action: PayloadAction<LocaleOverride>
) => {
  state.localeOverride = action.payload
}

const setLocaleLanguageOverride = (
  state: MetadataSliceState,
  action: PayloadAction<string | undefined>
) => {
  const language = action.payload || undefined
  state.localeOverride = {
    ...state.localeOverride,
    ...{ language },
  }
}

const setLocaleCountryOverride = (
  state: MetadataSliceState,
  action: PayloadAction<string | undefined>
) => {
  const country = action.payload || undefined
  state.localeOverride = {
    ...state.localeOverride,
    ...{ country },
  }
}

const setPreferenceCenters = (
  state: MetadataSliceState,
  action: PayloadAction<PreferenceCenter[]>
) => {
  state.preferenceCenters = action.payload
}

const setSupportInfo = (
  state: MetadataSliceState,
  action: PayloadAction<SupportInfo>
) => {
  state.supportInfo = action.payload
}

const MAX_FLAG_CHECKS = 10

const setFeatureFlag = (
  state: MetadataSliceState,
  action: PayloadAction<FeatureFlagEligibilityCheck>
) => {
  const flags = state.featureFlags ?? []
  state.featureFlags = [action.payload, ...flags.slice(0, MAX_FLAG_CHECKS)]
}

const MAX_CONFLICT_EVENTS = 10

const addConflictEvent = (
  state: MetadataSliceState,
  action: PayloadAction<ConflictEventWrapper>
) => {
  const events = state.conflictEvents ?? []
  state.conflictEvents = [
    action.payload,
    ...events.slice(0, MAX_CONFLICT_EVENTS),
  ]
}

const metadataReducers = {
  setLocaleOverride,
  setLocaleLanguageOverride,
  setLocaleCountryOverride,
  setPreferenceCenters,
  setSupportInfo,
  addConflictEvent,
}

export type MetadataSlice = Slice<
  MetadataSliceState,
  typeof metadataReducers,
  "metadata"
>

export const metadataSlice = createSlice({
  name: "metadata",
  initialState: initialState.metadata,
  reducers: metadataReducers,
  extraReducers: (builder) => {
    builder.addCase(thunks.loadLocaleOverride.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.loadLocaleOverride.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setLocaleOverride)
    )
    builder.addCase(thunks.loadLocaleOverride.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.setLocaleLanguageOverride.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.setLocaleLanguageOverride.fulfilled,
      (state, action) =>
        processFulfilledOperation(state, action, setLocaleLanguageOverride)
    )
    builder.addCase(
      thunks.setLocaleLanguageOverride.rejected,
      (state, action) => processRejectedOperation(state, action)
    )

    builder.addCase(thunks.setLocaleCountryOverride.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.setLocaleCountryOverride.fulfilled,
      (state, action) =>
        processFulfilledOperation(state, action, setLocaleCountryOverride)
    )
    builder.addCase(thunks.setLocaleCountryOverride.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.loadPreferenceCenters.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.loadPreferenceCenters.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setPreferenceCenters)
    )
    builder.addCase(thunks.loadPreferenceCenters.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.loadSupportInfo.pending, (state, action) => {
      processPendingOperation(state)
    })
    builder.addCase(thunks.loadSupportInfo.fulfilled, (state, action) => {
      processFulfilledOperation(state, action, setSupportInfo)
    })
    builder.addCase(thunks.loadSupportInfo.rejected, (state, action) => {
      processRejectedOperation(state, action)
    })

    builder.addCase(thunks.getFeatureFlag.pending, (state, action) => {
      processPendingOperation(state)
    })
    builder.addCase(thunks.getFeatureFlag.fulfilled, (state, action) => {
      processFulfilledOperation(state, action, setFeatureFlag)
    })
    builder.addCase(thunks.getFeatureFlag.rejected, (state, action) => {
      processRejectedOperation(state, action)
    })

    builder.addCase(thunks.trackScreen.pending, (state, action) => {
      processPendingOperation(state)
    })
    builder.addCase(thunks.trackScreen.fulfilled, (state, action) => {
      processFulfilledOperation(state, action, () => void 0)
    })
    builder.addCase(thunks.trackScreen.rejected, (state, action) => {
      processRejectedOperation(state, action)
    })

    builder.addCase(
      thunks.setDataCollectionEnabled.pending,
      (state, action) => {
        processPendingOperation(state)
      }
    )
    builder.addCase(
      thunks.setDataCollectionEnabled.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, () => {
          if (!state.supportInfo) return
          state.supportInfo.dataCollectionEnabled = action.payload
        })
      }
    )
    builder.addCase(
      thunks.setDataCollectionEnabled.rejected,
      (state, action) => {
        processRejectedOperation(state, action)
      }
    )
  },
})

/**
 * Contact slice
 */

export type ContactSliceState = Store["contact"]

const identifyContact = (
  state: ContactSliceState,
  action: PayloadAction<string>
): void => {
  state.name = action.payload
}

const resetContact = (
  state: ContactSliceState,
  action: PayloadAction<void>
): void => {
  state.name = undefined
  state.tags = []
  state.attributes = {}
  state.associated = []
  state.subscription_lists = {}
}

const addAssociatedChannel = (
  state: ContactSliceState,
  action: PayloadAction<AssociatedChannel>
): void => {
  state.associated.push(action.payload)
}

const setContactSubscriptions = (
  state: ContactSliceState,
  action: PayloadAction<ScopedSubscriptionListIdentifier>
): void => {
  state.subscription_lists = action.payload
}

const addContactSubscription = (
  state: ContactSliceState,
  action: PayloadAction<SubscriptionListPayload>
): void => {
  const { listId, scope } = action.payload
  const lists = { ...state.subscription_lists }
  const existingListScopes = lists[listId]
  if (!existingListScopes) {
    state.subscription_lists = {
      ...lists,
      [listId]: [scope],
    }
    return
  }
  if (!existingListScopes.includes(scope)) {
    state.subscription_lists = {
      ...lists,
      [listId]: [...existingListScopes, scope],
    }
  }
}

const removeContactSubscription = (
  state: ContactSliceState,
  action: PayloadAction<SubscriptionListPayload>
): void => {
  const { listId, scope } = action.payload
  if (
    !state.subscription_lists[listId] ||
    !state.subscription_lists[listId]?.includes(scope)
  ) {
    return
  }
  if (state.subscription_lists[listId]?.length === 1) {
    delete state.subscription_lists[listId]
    return
  }
  state.subscription_lists[listId] =
    state.subscription_lists[listId]?.filter(
      (existingScope) => existingScope !== scope
    ) ?? []
}

const contactReducers = {
  addTag,
  removeTag,
  setAttribute,
  removeAttribute,
  identifyContact,
  resetContact,
  setContactSubscriptions,
  addContactSubscription,
  removeContactSubscription,
}

export type ContactSlice = Slice<
  ContactSliceState,
  typeof contactReducers,
  "contact"
>

export const contactSlice = createSlice({
  name: "contact",
  initialState: initialState.contact,
  reducers: contactReducers,
  extraReducers: (builder) => {
    builder.addCase(thunks.addContactTag.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.addContactTag.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addTag)
    )
    builder.addCase(thunks.addContactTag.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.removeContactTag.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.removeContactTag.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeTag)
    )
    builder.addCase(thunks.removeContactTag.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.identifyContact.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.identifyContact.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, identifyContact)
    )
    builder.addCase(thunks.identifyContact.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.resetContact.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.resetContact.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, resetContact)
    )
    builder.addCase(thunks.resetContact.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.associateEmail.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.associateEmail.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addAssociatedChannel)
    )
    builder.addCase(thunks.associateEmail.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.associateSms.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.associateSms.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addAssociatedChannel)
    )
    builder.addCase(thunks.associateSms.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.associateOpen.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.associateOpen.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addAssociatedChannel)
    )
    builder.addCase(thunks.associateOpen.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.setContactAttribute.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.setContactAttribute.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setAttribute)
    )
    builder.addCase(thunks.setContactAttribute.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.removeContactAttribute.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.removeContactAttribute.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeAttribute)
    )
    builder.addCase(thunks.removeContactAttribute.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    /**
     * Contact Subscriptions
     */
    builder.addCase(thunks.loadContactSubscriptions.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.loadContactSubscriptions.fulfilled,
      (state, action) =>
        processFulfilledOperation(state, action, setContactSubscriptions)
    )
    builder.addCase(thunks.loadContactSubscriptions.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    builder.addCase(thunks.subscribeContact.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.subscribeContact.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addContactSubscription)
    )
    builder.addCase(thunks.subscribeContact.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    builder.addCase(thunks.unsubscribeContact.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.unsubscribeContact.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeContactSubscription)
    )
    builder.addCase(thunks.unsubscribeContact.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
  },
})

/**
 * Channel Slice
 */

export type ChannelSliceState = Store["channel"]

const setChannel = (
  state: ChannelSliceState,
  action: PayloadAction<ChannelInfo | null>
): void => {
  if (!action.payload) {
    delete state.id
    state.optIn = false

    return
  }

  state.id = action.payload.id
  state.optIn = action.payload.optedIn
}

const setOptIn = (
  state: ChannelSliceState,
  action: PayloadAction<boolean>
): void => {
  state.optIn = action.payload
}

const setSubscriptions = (
  state: ChannelSliceState,
  action: PayloadAction<string[]>
): void => {
  state.subscriptions = action.payload
}

const addSubscription = (
  state: ChannelSliceState,
  action: PayloadAction<string>
): void => {
  const subscriptions = new Set(state.subscriptions)
  subscriptions.add(action.payload)
  state.subscriptions = Array.from(subscriptions.values())
}

const removeSubscription = (
  state: ChannelSliceState,
  action: PayloadAction<string>
): void => {
  state.subscriptions = state.subscriptions.filter(
    (sub) => sub !== action.payload
  )
}

const channelReducers = {
  setChannel,
  setOptIn,
  addTag,
  removeTag,
  setAttribute,
  removeAttribute,
  setSubscriptions,
  addSubscription,
  removeSubscription,
}

export type ChannelSlice = Slice<
  ChannelSliceState,
  typeof channelReducers,
  "channel"
>

export const channelSlice = createSlice({
  name: "channel",
  initialState: initialState.channel,
  reducers: channelReducers,
  extraReducers: (builder) => {
    builder.addCase(thunks.loadChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.loadChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setChannel)
    )
    builder.addCase(thunks.loadChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    /**
     * Channel Registration
     */
    builder.addCase(thunks.createChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.createChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setChannel)
    )
    builder.addCase(thunks.createChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    builder.addCase(thunks.registerChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.registerChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setChannel)
    )
    builder.addCase(thunks.registerChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.optOutChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.optOutChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setOptIn)
    )
    builder.addCase(thunks.optOutChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    /**
     * Channel Tags
     */
    builder.addCase(thunks.addChannelTag.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.addChannelTag.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addTag)
    )
    builder.addCase(thunks.addChannelTag.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.removeChannelTag.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.removeChannelTag.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeTag)
    )
    builder.addCase(thunks.removeChannelTag.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    /**
     * Channel Attributes
     */
    builder.addCase(thunks.setChannelAttribute.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.setChannelAttribute.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setAttribute)
    )
    builder.addCase(thunks.setChannelAttribute.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.removeChannelAttribute.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.removeChannelAttribute.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeAttribute)
    )
    builder.addCase(thunks.removeChannelAttribute.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    /**
     * Channel Subscriptions
     */
    builder.addCase(thunks.loadChannelSubscriptions.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.loadChannelSubscriptions.fulfilled,
      (state, action) =>
        processFulfilledOperation(state, action, setSubscriptions)
    )
    builder.addCase(thunks.loadChannelSubscriptions.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    builder.addCase(thunks.subscribeChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.subscribeChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addSubscription)
    )
    builder.addCase(thunks.subscribeChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
    builder.addCase(thunks.unsubscribeChannel.pending, (state, action) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.unsubscribeChannel.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, removeSubscription)
    )
    builder.addCase(thunks.unsubscribeChannel.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    /** Clear Everything */
    builder.addCase(thunks.clearEverything.pending, (state) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.clearEverything.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, setChannel)
    )
    builder.addCase(thunks.clearEverything.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )
  },
})

/**
 * Analytics Slice
 */

export type AnalyticsSliceState = Store["analytics"]

const addAssociatedIdentifier = (
  state: AnalyticsSliceState,
  action: PayloadAction<AssociatedIdentifier>
): void => {
  state.associatedIdentifiers.push(action.payload)
}

const removeAssociatedIdentifier = (
  state: AnalyticsSliceState,
  action: PayloadAction<string>
): void => {
  state.associatedIdentifiers = state.associatedIdentifiers.filter(
    (identifier) => identifier.key !== action.payload
  )
}

const applyAssociatedIdentifiers = (
  state: AnalyticsSliceState,
  action: PayloadAction<AssociatedIdentifier[]>
): void => {
  state.associatedIdentifiers = action.payload
}

const getAssociatedIdentifiers = (
  state: AnalyticsSliceState,
  action: PayloadAction<AssociatedIdentifier[]>
): void => {
  state.associatedIdentifiers = action.payload
}

const analyticsReducers = {
  addAssociatedIdentifier,
  removeAssociatedIdentifier,
  applyAssociatedIdentifiers,
  getAssociatedIdentifiers,
}

export const analyticsSlice = createSlice({
  name: "analytics",
  initialState: initialState.analytics,
  reducers: analyticsReducers,
  extraReducers: (builder) => {
    builder.addCase(thunks.getAssociatedIdentifiers.pending, (state) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.getAssociatedIdentifiers.fulfilled,
      (state, action) => {
        processFulfilledOperation(state, action, getAssociatedIdentifiers)
      }
    )
    builder.addCase(thunks.getAssociatedIdentifiers.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.addAssociatedIdentifier.pending, (state) =>
      processPendingOperation(state)
    )
    builder.addCase(thunks.addAssociatedIdentifier.fulfilled, (state, action) =>
      processFulfilledOperation(state, action, addAssociatedIdentifier)
    )
    builder.addCase(thunks.addAssociatedIdentifier.rejected, (state, action) =>
      processRejectedOperation(state, action)
    )

    builder.addCase(thunks.removeAssociatedIdentifier.pending, (state) =>
      processPendingOperation(state)
    )
    builder.addCase(
      thunks.removeAssociatedIdentifier.fulfilled,
      (state, action) =>
        processFulfilledOperation(state, action, removeAssociatedIdentifier)
    )
    builder.addCase(
      thunks.removeAssociatedIdentifier.rejected,
      (state, action) => processRejectedOperation(state, action)
    )
  },
})
